import { memo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import type { ComponentProps } from 'react';
import { BoardColumn } from './BoardColumn';
import { columnDragId } from './drag-ids';
import { BOARD_COLUMN_WIDTH } from './board-layout';
import FadeContent from '../reactBits/FadeContent';

type SortableBoardColumnProps = ComponentProps<typeof BoardColumn> & {
  /** When false (the default), the column does not respond to drag.
   * BoardPage toggles this via the "Reorganize columns" mode so the
   * column body isn't an oversized drag handle by accident. */
  dragEnabled?: boolean;
  entranceDelay?: number;
  mobileActive?: boolean;
};

function SortableBoardColumnImpl({
  dragEnabled = false,
  entranceDelay,
  mobileActive = true,
  ...props
}: SortableBoardColumnProps) {
  const { column } = props;
  // Keep the opening stagger fixed: reordering a column must not replay it.
  const [initialEntranceDelay] = useState(entranceDelay);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: columnDragId(column._id),
    data: { type: 'column' },
    // Spelled out rather than `disabled: !dragEnabled`. The two are
    // equivalent — useSortable's `normalizeLocalDisabled` maps a bare boolean
    // to `{ draggable: bool, droppable: false }` for backwards compatibility
    // — but the boolean reads as "this column is disabled", which is wrong
    // and easy to act on. The column is only DRAGGABLE in reorganize mode; it
    // must always stay DROPPABLE so cards can be dropped into an empty column
    // or below the last card. Pinned by SortableBoardColumn.droppable.test.
    disabled: { draggable: !dragEnabled, droppable: false },
  });

  return (
    <Box
      ref={setNodeRef}
      data-testid={`board-column-${column._id}`}
      sx={{
        display: { xs: mobileActive ? 'block' : 'none', md: 'block' },
        // Share spare space equally while preserving readable lanes when
        // the board needs to scroll horizontally.
        width: BOARD_COLUMN_WIDTH,
        minWidth: BOARD_COLUMN_WIDTH,
        flexGrow: 1,
        flexShrink: 0,
        transform: CSS.Transform.toString(transform),
        transition,
        // When this column is the one being dragged, hide it entirely —
        // the DragOverlay in BoardCanvas renders the visible "floating"
        // preview. Without this, the in-place opacity sit looks crooked
        // and `position: sticky` on the header jitters with transform.
        opacity: isDragging ? 0 : 1,
        cursor: dragEnabled ? 'grab' : 'default',
      }}
      {...attributes}
      {...listeners}
    >
      {initialEntranceDelay === undefined ? (
        <BoardColumn {...props} />
      ) : (
        <FadeContent
          playOnMount
          duration={480}
          delay={initialEntranceDelay}
          data-board-entrance="lane"
        >
          <BoardColumn {...props} />
        </FadeContent>
      )}
    </Box>
  );
}

export const SortableBoardColumn = memo(SortableBoardColumnImpl);
